import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import ComicCard from "@/components/base/ComicCard";
import {
  comics,
  categories,
  getLatestComics,
  getTopComics,
} from "@/mocks/comics";

export default function Home() {
  const location = useLocation();
  const latestComics = getLatestComics(6);
  const topComics = getTopComics(10);
  const recommendedComics = comics.slice(0, 6);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const siteUrl = "https://hanhanmanhua.com";

  useSEO({
    title: "汗汗漫画 - 更新最快的漫画网 | 好看的免费漫画大全 | 无遮挡韩漫",
    description:
      "汗汗漫画以更新速度最快为宣传点，同时提供好看的免费漫画大全和无遮挡韩漫，追求在内容时效性和阅读体验上做到极致。涵盖韩漫、日漫、国漫等海量免费漫画资源。",
    keywords:
      "汗汗漫画,更新最快的漫画网,好看的免费漫画大全,无遮挡韩漫,韩漫,日漫,国漫,免费漫画,在线漫画",
    canonicalUrl: siteUrl,
    ogTitle: "汗汗漫画 - 更新最快的漫画网 | 好看的免费漫画大全",
    ogDescription:
      "汗汗漫画以更新速度最快为宣传点，提供好看的免费漫画大全和无遮挡韩漫，追求内容时效性和阅读体验的极致。",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "汗汗漫画",
        url: siteUrl,
        description:
          "更新最快的漫画网，提供好看的免费漫画大全，无遮挡韩漫日漫国漫",
        contactPoint: {
          "@type": "ContactPoint",
          email: "contact@hanhanmanhua.com",
          contactType: "customer service",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "汗汗漫画",
        url: siteUrl,
        description:
          "更新最快的漫画网，提供好看的免费漫画大全，无遮挡韩漫日漫国漫",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${siteUrl}/search?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "汗汗漫画 - 首页",
        description:
          "汗汗漫画，更新最快的漫画网，提供好看的免费漫画大全，无遮挡韩漫日漫国漫，极速更新永久免费在线看。",
        url: siteUrl,
        isPartOf: {
          "@type": "WebSite",
          name: "汗汗漫画",
        },
      },
    ],
  });

  return (
    <main>
      {/* Hero Section */}
      <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20vibrant%20manga%20style%20illustration%20with%20dynamic%20action%20lines%20and%20soft%20gradient%20background%2C%20warm%20orange%20and%20coral%20tones%20blending%20with%20cream%20white%2C%20stylized%20comic%20burst%20effects%20and%20speed%20lines%2C%20abstract%20artistic%20composition%20with%20manga%20panel%20motifs%2C%20clean%20modern%20aesthetic%2C%20no%20characters%2C%20subtle%20paper%20texture%2C%20energetic%20yet%20elegant%20atmosphere&width=1440&height=600&seq=hero-bg-01&orientation=landscape"
          alt="汗汗漫画背景"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>

        <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight leading-tight">
            更新最快的漫画网
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-6 max-w-xl mx-auto leading-relaxed">
            汗汗漫画 — 海量韩漫、日漫、国漫免费在线阅读，无遮挡极致体验，热门漫画同步更新
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/25"
            >
              立即浏览
            </Link>
            <Link
              to="/category/hanman"
              className="px-8 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-semibold text-sm hover:bg-white/25 transition-all duration-200 cursor-pointer whitespace-nowrap border border-white/30"
            >
              韩漫专区
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">50,000+</p>
              <p className="text-xs text-white/60 mt-1">漫画资源</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">200万+</p>
              <p className="text-xs text-white/60 mt-1">日活用户</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">24h</p>
              <p className="text-xs text-white/60 mt-1">极速更新</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full px-4 md:px-8 py-8 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 text-sm font-medium cursor-pointer whitespace-nowrap border border-background-200/50"
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Ranking Section */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">
                热门排行榜
              </h2>
              <span className="text-xs text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded-full font-medium">
                TOP 10
              </span>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              查看更多 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {topComics.slice(0, 10).map((comic, index) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group border border-background-200/30"
                data-product-shop
              >
                <span
                  className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                    index < 3
                      ? "bg-primary-500 text-white"
                      : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {comic.latestChapter}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">
                最新更新
              </h2>
              <span className="flex items-center gap-1 text-xs text-accent-600">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
                实时更新
              </span>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              全部漫画 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {latestComics.map((comic) => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Recommended Section */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">
              编辑推荐
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              更多推荐 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {recommendedComics.map((comic) => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-8 py-14 bg-background-900">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">
            开始你的漫画之旅
          </h2>
          <p className="text-background-400 text-sm mb-6 max-w-md mx-auto">
            加入200万+漫画爱好者，发现更多精彩漫画，体验更新最快的阅读服务
          </p>
          <Link
            to="/category/all"
            className="inline-block px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            立即探索
          </Link>
        </div>
      </section>
    </main>
  );
}